/**
 * Chat de atendimento — ponto único de acesso ao widget.
 *
 * O provedor é escolhido por `REACT_APP_CHAT_PROVIDER`:
 *   'cnlchat' (padrão) — widget próprio, carregado em routes/Route.tsx
 *   'tawk'             — Tawk.to, comportamento anterior
 *
 * Rollback em produção: trocar a variável para 'tawk' e publicar. Nenhum
 * arquivo de código precisa ser alterado — a carga do script e todos os
 * pontos que abrem o chat seguem esta mesma chave.
 */
export type ChatProvider = 'cnlchat' | 'tawk';

export const chatProvider: ChatProvider =
  process.env.REACT_APP_CHAT_PROVIDER === 'tawk' ? 'tawk' : 'cnlchat';

export const isTawk = chatProvider === 'tawk';
export const isCnlChat = chatProvider === 'cnlchat';

/**
 * Abre o chat. `mensagemInicial` só é usada no Tawk: o CNL Chat ainda não
 * expõe API para abrir já com uma mensagem (a API pública é open/close/
 * setIdentity/clearIdentity).
 */
export function openSupportChat(mensagemInicial?: string): void {
  if (isTawk) {
    const tawk = (window as any).Tawk_API;
    try {
      tawk?.maximize?.();
      if (mensagemInicial) tawk?.addEvent?.('chatStart', { message: mensagemInicial });
    } catch {
      // Widget indisponível neste instante: nada a fazer.
    }
    return;
  }

  // O loader é assíncrono: um clique antes de ele carregar não pode quebrar.
  (window as any).CNLChat?.open?.();
}

/**
 * Há atendente disponível agora?
 *
 * No Tawk a resposta vem de `getStatus()`, e sem atendente o hospedeiro precisa
 * oferecer outro caminho. No CNL Chat isso não é necessário: o próprio widget
 * trata o horário de atendimento e apresenta os formulários devidos fora do
 * expediente. Por isso devolvemos `true` — a decisão é dele.
 */
export function isSupportChatOnline(): boolean {
  if (isTawk) {
    const tawk = (window as any).Tawk_API;
    return !!tawk && typeof tawk.getStatus === 'function' && tawk.getStatus() === 'online';
  }

  return true;
}
